import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { quoteApi } from "@/lib/adminApi";

export function QuoteList({ onOpen, refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await quoteApi.list();
      setItems(data);
    } catch {
      toast.error("Failed to load quotes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [refreshKey]);

  const remove = async (id) => {
    if (!window.confirm("Delete this quote draft?")) return;
    try {
      await quoteApi.remove(id);
      toast.success("Deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading quotes&hellip;
      </div>
    );
  }

  if (items.length === 0) {
    return <p className="py-6 text-sm text-muted-foreground">No quotes yet.</p>;
  }

  return (
    <div className="overflow-x-auto rounded-sm border border-border" data-testid="quote-list">
      <table className="w-full min-w-[900px] text-left text-sm">
        <thead className="bg-muted/60 text-xs uppercase tracking-[0.16em] text-muted-foreground">
          <tr>
            <th className="px-3 py-2">Provider</th>
            <th className="px-3 py-2">Service</th>
            <th className="px-3 py-2">Final $</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">v</th>
            <th className="px-3 py-2">Approved</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((q) => (
            <tr key={q.id} className="border-t border-border align-top">
              <td className="px-3 py-2 font-medium text-foreground">{q.providerLegalEntity || q.tradingName || <span className="text-muted-foreground">— unnamed —</span>}</td>
              <td className="px-3 py-2 text-muted-foreground">{q.selectedServiceCode || "—"}</td>
              <td className="px-3 py-2 text-muted-foreground">{q.proposedFinalPrice ? `$${q.proposedFinalPrice}` : "—"}</td>
              <td className="px-3 py-2 text-muted-foreground">{q.status}</td>
              <td className="px-3 py-2 text-muted-foreground">{q.version}</td>
              <td className="px-3 py-2 text-muted-foreground">{q.ownerApproved ? "yes" : "no"}</td>
              <td className="px-3 py-2 text-right">
                <div className="flex justify-end gap-1">
                  <Button size="sm" variant="outline" onClick={() => onOpen(q)} className="rounded-sm" data-testid={`quote-open-${q.id}`}>
                    Open
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(q.id)} className="rounded-sm">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
